import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronRight, ArrowLeft } from "lucide-react";

const ROLES = ["Admin", "SrManager", "CreativeDirector", "AssociateCreativeDirector", "ContentProducer", "SocialMediaManager", "Member"];

export default function AdminOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pillars");
  const isAdmin = user?.role?.toLowerCase() === "admin";

  if (!isAdmin) {
    return <div className="p-8 text-center text-[var(--t3)]">Admin only</div>;
  }

  return (
    <div className="dm-page-container max-w-[1100px] mx-auto" style={{ padding: 24 }}>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/onboarding")} className="p-2 text-[var(--t3)] hover:text-[var(--t1)]"><ArrowLeft size={18} /></button>
        <h1 className="font-heading text-xl text-[var(--t1)] tracking-wide">ONBOARDING ADMIN</h1>
      </div>

      <div className="flex gap-1 mb-6 border-b border-[var(--bdr)]">
        {[
          { k: "pillars", l: "Pillars & Lessons" },
          { k: "tracks", l: "Role Tracks" },
          { k: "trainees", l: "Trainees" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium ${tab === t.k ? "text-[var(--r)] border-b-2 border-[var(--r)]" : "text-[var(--t3)] hover:text-[var(--t1)]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "pillars" && <PillarsAdmin />}
      {tab === "tracks" && <TracksAdmin />}
      {tab === "trainees" && <TraineesAdmin />}
    </div>
  );
}

// ════════════════════════════════════════
// TRAINEES ADMIN — see all users' onboarding progress
// ════════════════════════════════════════
function TraineesAdmin() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/onboarding/team-progress").then(r => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center text-[var(--t3)]">Loading...</div>;

  const filtered = statusFilter === "all" ? rows :
    statusFilter === "not_started" ? rows.filter(r => r.current_status === "not_started" || r.passed_count === 0) :
    statusFilter === "in_progress" ? rows.filter(r => r.passed_count > 0 && r.passed_count < r.total_count) :
    statusFilter === "stuck" ? rows.filter(r => r.current_status === "failed") :
    statusFilter === "done" ? rows.filter(r => r.total_count > 0 && r.passed_count === r.total_count) :
    rows;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";
  const daysAgo = (d) => {
    if (!d) return null;
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap">
        {[
          { k: "all", l: `All (${rows.length})` },
          { k: "not_started", l: "Not Started" },
          { k: "in_progress", l: "In Progress" },
          { k: "stuck", l: "Stuck / Failed" },
          { k: "done", l: "Completed" },
        ].map(f => (
          <button key={f.k} onClick={() => setStatusFilter(f.k)}
            className={`px-3 py-1 rounded-full text-xs ${statusFilter === f.k ? "bg-[var(--t1)] text-white" : "bg-[var(--s2)] text-[var(--t3)] hover:text-[var(--t1)]"}`}>
            {f.l}
          </button>
        ))}
      </div>

      <div className="bg-[var(--s1)] border border-[var(--bdr)] rounded-lg overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_2fr_1fr_1fr_1fr] gap-2 px-4 py-2 border-b border-[var(--bdr)] text-[10px] font-mono uppercase text-[var(--t3)] tracking-wider">
          <span>Name</span><span>Role</span><span>Current Pillar</span><span>Progress</span><span>Status</span><span>Last Active</span>
        </div>
        {filtered.length === 0 ? (
          <p className="text-center text-[var(--t3)] py-10 text-sm">No trainees match this filter.</p>
        ) : filtered.map(r => {
          const pct = r.total_count ? Math.round((r.passed_count / r.total_count) * 100) : 0;
          const days = daysAgo(r.last_activity);
          const stuck = r.current_status === "failed" || (days !== null && days > 5 && r.passed_count < r.total_count);
          return (
            <div key={r.user_id} onClick={() => navigate(`/team/${r.user_id}`)}
              className="grid grid-cols-[2fr_1fr_2fr_1fr_1fr_1fr] gap-2 px-4 py-3 border-b border-[var(--bdr)] hover:bg-[var(--s2)] cursor-pointer items-center">
              <div>
                <p className="text-sm text-[var(--t1)] font-medium">{r.name}</p>
                <p className="text-[10px] text-[var(--t3)]">{r.email}</p>
              </div>
              <span className="text-xs text-[var(--t2)]">{r.role}</span>
              <span className="text-xs text-[var(--t2)] truncate">{r.current_pillar || "—"}</span>
              <div>
                <div className="h-1 bg-[var(--s2)] rounded-full overflow-hidden mb-0.5">
                  <div className="h-full bg-[var(--g)]" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-[var(--t3)]">{r.passed_count}/{r.total_count}</p>
              </div>
              <span className={`text-xs ${stuck ? "text-[var(--r)]" : r.current_status === "passed" ? "text-[var(--g)]" : "text-[var(--t3)]"}`}>
                {r.current_status === "failed" ? "❌ Stuck" :
                 r.passed_count === r.total_count && r.total_count > 0 ? "✅ Done" :
                 r.current_status === "in_progress" ? "▶ Active" :
                 "Not started"}
              </span>
              <span className="text-xs text-[var(--t3)]">
                {days === null ? "—" : days === 0 ? "Today" : `${days}d ago`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════
// PILLARS ADMIN
// ════════════════════════════════════════
function PillarsAdmin() {
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [editPillar, setEditPillar] = useState(null);

  useEffect(() => { load(); }, []);
  const [lessonCounts, setLessonCounts] = useState({});
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem("onboarding_defaults_dismissed") === "1");

  const load = async () => {
    try {
      const r = await api.get("/onboarding/pillars");
      const list = r.data || [];
      setPillars(list);
      // Fetch lesson counts per pillar
      const counts = {};
      await Promise.all(list.map(async (p) => {
        try {
          const lr = await api.get(`/onboarding/pillars/${p.pillar_id}/lessons`);
          counts[p.pillar_id] = (lr.data || []).length;
        } catch { counts[p.pillar_id] = 0; }
      }));
      setLessonCounts(counts);
    } catch {}
    setLoading(false);
  };

  const toggle = (id) => {
    const next = new Set(openIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setOpenIds(next);
  };

  const dismissBanner = () => {
    localStorage.setItem("onboarding_defaults_dismissed", "1");
    setBannerDismissed(true);
  };

  if (loading) return <div className="text-center text-[var(--t3)]">Loading...</div>;

  const totalLessons = Object.values(lessonCounts).reduce((a, b) => a + b, 0);
  const isFirstTime = totalLessons === 0 && pillars.length > 0 && !bannerDismissed;

  return (
    <div>
      {isFirstTime && (
        <div className="mb-4 p-4 bg-[var(--c)]/5 border border-[var(--c)]/30 rounded-lg flex items-start gap-3">
          <span style={{ fontSize: 20 }}>👋</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[var(--t1)] mb-1">Welcome! These are starter pillars.</p>
            <p className="text-xs text-[var(--t3)]">The 10 pillars below are defaults to get you going. Edit, delete, or reorder them to match your agency. Add at least 1 lesson per pillar before letting trainees in. Record a Loom walking through one of your best decks from the <strong className="text-[var(--t2)]">Decks</strong> library — that's your first lesson.</p>
          </div>
          <button onClick={dismissBanner} className="text-[var(--t3)] hover:text-[var(--t1)]"><X size={14} /></button>
        </div>
      )}
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 px-3 py-1.5 bg-[var(--r)] text-white text-xs font-bold rounded-lg">
          <Plus size={12} /> Add Pillar
        </button>
      </div>
      <div className="space-y-2">
        {pillars.map(p => {
          const count = lessonCounts[p.pillar_id] ?? 0;
          const isEmpty = count === 0;
          return (
            <div key={p.pillar_id} className={`bg-[var(--s1)] border rounded-lg overflow-hidden ${isEmpty ? "border-[var(--a)]/30" : "border-[var(--bdr)]"}`}>
              <div className="flex items-center px-4 py-3">
                <button onClick={() => toggle(p.pillar_id)} className="flex-1 flex items-center gap-3 text-left">
                  {openIds.has(p.pillar_id) ? <ChevronDown size={16} color="var(--t3)" /> : <ChevronRight size={16} color="var(--t3)" />}
                  <span style={{ fontSize: 16 }}>{p.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--t1)]">{p.title}</p>
                    <p className="text-[10px] text-[var(--t3)]">{p.description}</p>
                  </div>
                  <span className={`text-[10px] font-mono ${isEmpty ? "text-[var(--a)]" : "text-[var(--t3)]"}`}>
                    {isEmpty ? "⚠ No lessons" : `${count} lesson${count === 1 ? "" : "s"}`}
                  </span>
                </button>
                <button onClick={() => setEditPillar(p)} className="p-1.5 text-[var(--t3)] hover:text-[var(--t1)]"><Pencil size={12} /></button>
              </div>
              {openIds.has(p.pillar_id) && <LessonsAdmin pillarId={p.pillar_id} onLessonChange={load} />}
            </div>
          );
        })}
      </div>
      {showAdd && <PillarEditModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editPillar && <PillarEditModal pillar={editPillar} onClose={() => setEditPillar(null)} onSaved={() => { setEditPillar(null); load(); }} />}
    </div>
  );
}

function LessonsAdmin({ pillarId, onLessonChange }) {
  const [lessons, setLessons] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editLesson, setEditLesson] = useState(null);

  useEffect(() => { load(); }, [pillarId]);
  const load = async () => {
    try { const r = await api.get(`/onboarding/pillars/${pillarId}/lessons`); setLessons(r.data || []); } catch {}
    if (onLessonChange) onLessonChange();
  };

  return (
    <div className="border-t border-[var(--bdr)] bg-[var(--s2)] p-4 space-y-2">
      {lessons.length === 0 ? (
        <p className="text-[var(--t3)] text-xs text-center py-2">No lessons yet</p>
      ) : lessons.map(l => (
        <div key={l.lesson_id} className="flex items-center gap-2 px-3 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-lg">
          <span style={{ fontSize: 12 }}>{l.type === "video" ? "🎥" : l.type === "physical" ? "🧑‍🏫" : "📖"}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[var(--t1)] truncate">{l.title}</p>
            <p className="text-[10px] text-[var(--t3)]">{l.duration_minutes}m{l.loom_url ? " · Loom" : ""}{l.deck_view_url ? " · Deck" : ""}{l.deck_edit_url ? " · Edit" : ""}</p>
          </div>
          <button onClick={() => setEditLesson(l)} className="p-1 text-[var(--t3)] hover:text-[var(--t1)]"><Pencil size={11} /></button>
        </div>
      ))}
      <button onClick={() => setShowAdd(true)} className="w-full py-2 border border-dashed border-[var(--bdr)] rounded-lg text-xs text-[var(--t3)] hover:text-[var(--t1)] hover:border-[var(--bdrh)]">
        + Add Lesson
      </button>
      {showAdd && <LessonEditModal pillarId={pillarId} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editLesson && <LessonEditModal lesson={editLesson} pillarId={pillarId} onClose={() => setEditLesson(null)} onSaved={() => { setEditLesson(null); load(); }} />}
    </div>
  );
}

function PillarEditModal({ pillar, onClose, onSaved }) {
  const [form, setForm] = useState(pillar || { title: "", description: "", icon: "📚", order: 99 });
  const isEdit = !!pillar;
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      if (isEdit) await api.put(`/onboarding/pillars/${pillar.pillar_id}`, form);
      else await api.post("/onboarding/pillars", form);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete pillar (and all its lessons)?")) return;
    try { await api.delete(`/onboarding/pillars/${pillar.pillar_id}`); toast.success("Deleted"); onSaved(); } catch { toast.error("Failed"); }
  };
  const inputCls = "w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none";
  return (
    <Modal onClose={onClose} title={isEdit ? "Edit Pillar" : "Add Pillar"}>
      <div className="space-y-3">
        <div><label className="text-xs text-[var(--t2)] block mb-1">Title</label><input className={inputCls} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Icon (emoji)</label><input className={inputCls} value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} /></div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Description</label><textarea className={inputCls} rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Order</label><input className={inputCls} type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 99 })} /></div>
        <div className="flex gap-2 pt-2">
          <button onClick={save} className="flex-1 py-2 bg-[var(--r)] text-white font-bold rounded-lg text-sm">Save</button>
          {isEdit && <button onClick={del} className="px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-[var(--r)]"><Trash2 size={14} /></button>}
        </div>
      </div>
    </Modal>
  );
}

function LessonEditModal({ lesson, pillarId, onClose, onSaved }) {
  const [form, setForm] = useState(lesson || {
    pillar_id: pillarId, title: "", description: "", type: "video",
    loom_url: "", deck_view_url: "", deck_edit_url: "",
    physical_details: "", reading_url: "", duration_minutes: 0, order: 99
  });
  const isEdit = !!lesson;
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      if (isEdit) await api.put(`/onboarding/lessons/${lesson.lesson_id}`, form);
      else await api.post("/onboarding/lessons", form);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this lesson?")) return;
    try { await api.delete(`/onboarding/lessons/${lesson.lesson_id}`); toast.success("Deleted"); onSaved(); } catch { toast.error("Failed"); }
  };
  const inputCls = "w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none";
  return (
    <Modal onClose={onClose} title={isEdit ? "Edit Lesson" : "Add Lesson"}>
      <div className="space-y-3">
        <div><label className="text-xs text-[var(--t2)] block mb-1">Title *</label><input className={inputCls} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., How we won the H&M retainer" /></div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Type</label>
          <select className={inputCls} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            <option value="video">🎥 Video lesson</option>
            <option value="physical">🧑‍🏫 In-person session</option>
            <option value="reading">📖 Reading material</option>
          </select>
        </div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Duration (min)</label><input className={inputCls} type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 0 })} /></div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Description</label><textarea className={inputCls} rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        {form.type === "video" && (
          <div><label className="text-xs text-[var(--t2)] block mb-1">Loom / YouTube URL</label><input className={inputCls} value={form.loom_url} onChange={e => setForm({ ...form, loom_url: e.target.value })} placeholder="https://loom.com/share/..." /></div>
        )}
        {form.type === "physical" && (
          <div><label className="text-xs text-[var(--t2)] block mb-1">Session details</label><textarea className={inputCls} rows={2} value={form.physical_details} onChange={e => setForm({ ...form, physical_details: e.target.value })} placeholder="Friday 3pm, meeting room A, led by Dhruv" /></div>
        )}
        {form.type === "reading" && (
          <div><label className="text-xs text-[var(--t2)] block mb-1">Reading URL</label><input className={inputCls} value={form.reading_url} onChange={e => setForm({ ...form, reading_url: e.target.value })} /></div>
        )}
        <div className="border-t border-[var(--bdr)] pt-3">
          <p className="text-xs text-[var(--t2)] mb-2 font-semibold">Linked Deck (optional)</p>
          <div className="space-y-2">
            <div><label className="text-[10px] text-[var(--t3)] block mb-1">📄 View link (read-only original)</label><input className={inputCls} value={form.deck_view_url} onChange={e => setForm({ ...form, deck_view_url: e.target.value })} placeholder="https://canva.link/..." /></div>
            <div><label className="text-[10px] text-[var(--t3)] block mb-1">✏️ Edit link (copy for trainee to practice)</label><input className={inputCls} value={form.deck_edit_url} onChange={e => setForm({ ...form, deck_edit_url: e.target.value })} placeholder="https://canva.com/.../edit (a COPY of the deck)" /></div>
          </div>
        </div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Order</label><input className={inputCls} type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 99 })} /></div>
        <div className="flex gap-2 pt-2">
          <button onClick={save} className="flex-1 py-2 bg-[var(--r)] text-white font-bold rounded-lg text-sm">Save</button>
          {isEdit && <button onClick={del} className="px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-[var(--r)]"><Trash2 size={14} /></button>}
        </div>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════
// TRACKS ADMIN
// ════════════════════════════════════════
function TracksAdmin() {
  const [tracks, setTracks] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [editTrack, setEditTrack] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [t, p] = await Promise.all([api.get("/onboarding/tracks"), api.get("/onboarding/pillars")]);
      setTracks(t.data || []);
      setPillars(p.data || []);
    } catch {}
  };

  const pillarMap = Object.fromEntries(pillars.map(p => [p.pillar_id, p]));

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 px-3 py-1.5 bg-[var(--r)] text-white text-xs font-bold rounded-lg">
          <Plus size={12} /> New Track
        </button>
      </div>
      <p className="text-xs text-[var(--t3)] mb-3">Define which pillars each role goes through, in what order.</p>
      <div className="space-y-2">
        {tracks.length === 0 ? (
          <p className="text-center text-[var(--t3)] text-sm py-8">No tracks yet. Trainees will see all pillars in default order until you create a track for their role.</p>
        ) : tracks.map(t => (
          <div key={t.track_id} className="bg-[var(--s1)] border border-[var(--bdr)] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-[var(--t1)]">{t.title}</p>
                <p className="text-[10px] text-[var(--t3)]">{t.role} · {t.duration_days} days</p>
              </div>
              <button onClick={() => setEditTrack(t)} className="p-1.5 text-[var(--t3)] hover:text-[var(--t1)]"><Pencil size={12} /></button>
            </div>
            <div className="flex flex-wrap gap-1">
              {(t.pillar_sequence || []).map((pid, i) => {
                const p = pillarMap[pid];
                if (!p) return null;
                return (
                  <span key={pid} className="px-2 py-1 bg-[var(--s2)] rounded text-xs text-[var(--t2)]">
                    {i + 1}. {p.icon} {p.title}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {showAdd && <TrackEditModal pillars={pillars} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editTrack && <TrackEditModal track={editTrack} pillars={pillars} onClose={() => setEditTrack(null)} onSaved={() => { setEditTrack(null); load(); }} />}
    </div>
  );
}

function TrackEditModal({ track, pillars, onClose, onSaved }) {
  const [form, setForm] = useState(track || { role: "Member", title: "", pillar_sequence: [], duration_days: 30 });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const isEdit = !!track;

  const togglePillar = (pid) => {
    const seq = [...form.pillar_sequence];
    const idx = seq.indexOf(pid);
    if (idx === -1) seq.push(pid);
    else seq.splice(idx, 1);
    setForm({ ...form, pillar_sequence: seq });
  };
  const move = (i, dir) => {
    const seq = [...form.pillar_sequence];
    const newIdx = i + dir;
    if (newIdx < 0 || newIdx >= seq.length) return;
    [seq[i], seq[newIdx]] = [seq[newIdx], seq[i]];
    setForm({ ...form, pillar_sequence: seq });
  };
  // HTML5 drag-reorder — augments the ▲▼ buttons for faster bulk reordering.
  const handleDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(i));
  };
  const handleDragOver = (i) => (e) => { e.preventDefault(); setDragOverIdx(i); };
  const handleDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null); setDragOverIdx(null);
    if (from == null || from === i) return;
    const seq = [...form.pillar_sequence];
    const [moved] = seq.splice(from, 1);
    seq.splice(i, 0, moved);
    setForm({ ...form, pillar_sequence: seq });
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (form.pillar_sequence.length === 0) return toast.error("Pick at least one pillar");
    try {
      if (isEdit) await api.put(`/onboarding/tracks/${track.track_id}`, form);
      else await api.post("/onboarding/tracks", form);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Failed"); }
  };
  const del = () => {
    const title = form.title || "track";
    let undone = false;
    // Close modal + trigger reload optimistically, show 5s undo
    onClose();
    toast(`Deleted: ${title}`, {
      duration: 5000,
      action: { label: "Undo", onClick: () => { undone = true; } },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/onboarding/tracks/${track.track_id}`); onSaved(); }
          catch { toast.error("Delete failed"); }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/onboarding/tracks/${track.track_id}`); onSaved(); }
          catch { toast.error("Delete failed"); }
        }, 50);
      },
    });
  };

  const pillarMap = Object.fromEntries(pillars.map(p => [p.pillar_id, p]));
  const available = pillars.filter(p => !form.pillar_sequence.includes(p.pillar_id));
  const inputCls = "w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none";

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit Track" : "New Track"}>
      <div className="space-y-3">
        <div><label className="text-xs text-[var(--t2)] block mb-1">Title</label><input className={inputCls} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., SMM 30-Day Onboarding" /></div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Role</label>
          <select className={inputCls} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} disabled={isEdit}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-[var(--t2)] block mb-1">Duration (days)</label><input className={inputCls} type="number" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: parseInt(e.target.value) || 30 })} /></div>

        <div>
          <label className="text-xs text-[var(--t2)] block mb-1">Pillar sequence (in order)</label>
          <div className="space-y-1 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg p-2 min-h-[60px]">
            {form.pillar_sequence.length === 0 ? (
              <p className="text-[var(--t3)] text-xs text-center py-2">No pillars added. Pick from below.</p>
            ) : form.pillar_sequence.map((pid, i) => {
              const p = pillarMap[pid];
              if (!p) return null;
              const isOver = dragOverIdx === i && dragIdx !== i;
              return (
                <div key={pid}
                  draggable
                  onDragStart={handleDragStart(i)}
                  onDragOver={handleDragOver(i)}
                  onDrop={handleDrop(i)}
                  onDragEnd={handleDragEnd}
                  className="flex items-center gap-2 px-2 py-1.5 bg-[var(--s1)] rounded cursor-grab active:cursor-grabbing transition-colors"
                  style={{
                    outline: isOver ? "2px solid #3b82f6" : "none",
                    opacity: dragIdx === i ? 0.4 : 1,
                  }}
                  title="Drag to reorder"
                >
                  <span className="text-[var(--t3)] text-xs w-4 select-none" aria-hidden>⋮⋮</span>
                  <span className="text-[var(--t3)] text-xs w-5">{i + 1}.</span>
                  <span style={{ fontSize: 14 }}>{p.icon}</span>
                  <span className="flex-1 text-sm text-[var(--t1)]">{p.title}</span>
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-[var(--t3)] disabled:opacity-30" title="Move up">▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === form.pillar_sequence.length - 1} className="p-1 text-[var(--t3)] disabled:opacity-30" title="Move down">▼</button>
                  <button onClick={() => togglePillar(pid)} className="p-1 text-[var(--r)]" title="Remove"><X size={12} /></button>
                </div>
              );
            })}
          </div>
        </div>

        {available.length > 0 && (
          <div>
            <label className="text-xs text-[var(--t2)] block mb-1">Add pillars</label>
            <div className="flex flex-wrap gap-1">
              {available.map(p => (
                <button key={p.pillar_id} onClick={() => togglePillar(p.pillar_id)}
                  className="px-2 py-1 bg-[var(--s2)] border border-[var(--bdr)] rounded text-xs text-[var(--t2)] hover:text-[var(--t1)]">
                  + {p.icon} {p.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={save} className="flex-1 py-2 bg-[var(--r)] text-white font-bold rounded-lg text-sm">Save</button>
          {isEdit && <button onClick={del} className="px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-[var(--r)]"><Trash2 size={14} /></button>}
        </div>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════
// SHARED MODAL
// ════════════════════════════════════════
function Modal({ title, children, onClose }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 20, width: "min(500px, 92vw)", maxHeight: "85vh", overflow: "auto" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[var(--t1)]">{title}</h3>
          <button onClick={onClose} className="p-1 text-[var(--t3)] hover:text-[var(--t1)]"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
